visa-navigation {
  display: block;
  font-family: "Visa Dialect", sans-serif;
  width: 100%;
  overflow-x: hidden; /* Prevent horizontal scrolling at the component level */

  /* Theme variables for light/dark mode */
  --text-color-light: #1a1a1a;
  --text-color-dark: #ffffff;
  --text-color-light-rgb: 22, 22, 22;
  --text-color-dark-rgb: 255, 255, 255;
  --logo-fill-light: #1434cb;
  --logo-fill-dark: #ffffff;
  --text-color: var(--text-color-light); /* Default to light mode */
  --text-color-rgb: var(--text-color-light-rgb); /* Default to light mode */
  --logo-fill: var(--logo-fill-light); /* Default to light mode */
}

#visa-navigation-header-height {
  margin-top: 72px;
  @media (max-width: 1023px) {
    margin-top: 64px;
  }
}

.visa-navigation-navigation {
  background-color: var(--nav-background-color); /* Default to transparent background */
  display: flex;
  align-items: center;
  // padding: 16px 48px;
  height: 72px;
  position: absolute; /* Position absolutely */
  top: 0;
  left: 0;
  width: 100%;
  max-width: 100vw; /* Prevent horizontal overflow */
  box-sizing: border-box; /* Include padding in width calculation */
  overflow: visible; /* Allow hover effects to extend beyond the navigation container */
  z-index: 9999; /* Highest z-index to ensure it stays above all other content */
  transition:
    background-color 0.3s ease,
    box-shadow 0.3s ease; /* Smooth transition for background and shadow */

  /* Sticky header styles */
  &.visa-navigation-sticky {
    position: fixed;
    top: 0;
    z-index: 10000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); /* Add shadow when sticky */

    &.visa-navigation-theme-dark {
      background-color: #000000; /* Black background when sticky in dark mode */
      --nav-background-color: #000000;
    }

    &.visa-navigation-theme-light {
      background-color: #ffffff; /* White background when sticky in light mode */
      --nav-background-color: #ffffff;
    }
  }

  /* Theme defaults */
  &.visa-navigation-theme-dark {
    --text-color: var(--text-color-dark);
    --text-color-rgb: var(--text-color-dark-rgb);
    --logo-fill: var(--logo-fill-dark);
    --nav-background-color: transparent; /* Transparent background by default */
  }
  &.visa-navigation-theme-light {
    --text-color: var(--text-color-light);
    --text-color-rgb: var(--text-color-light-rgb);
    --logo-fill: var(--logo-fill-light);
    --nav-background-color: transparent; /* Transparent background by default */

    /* Special case: blended header in light mode should have white text and logo when no menu is open and not sticky */
    &.visa-navigation-pos-absolute:not(.visa-navigation-menu-open):not(.visa-navigation-sticky) {
      --text-color: #ffffff; /* White text for blended light mode */
      --text-color-rgb: 255, 255, 255;
      background: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 100%);
      --logo-fill: #ffffff; /* White logo for blended light mode */
    }
  }

  &.visa-navigation-theme-dark {
    &.visa-navigation-pos-relative {
      position: relative;
      --nav-background-color: black; /* Relative positioning for dark theme */
    }
    &.visa-navigation-pos-absolute {
      position: absolute; /* Absolute positioning for dark theme */
    }
  }
  &.visa-navigation-theme-light {
    &.visa-navigation-pos-relative {
      position: relative;
      --nav-background-color: white; /* Relative positioning for light theme */
    }
    &.visa-navigation-pos-absolute {
      position: absolute; /* Absolute positioning for light theme */
    }
  }

  /* Menu open states - applies to both sticky and non-sticky */
  &.visa-navigation-menu-open {
    &.visa-navigation-theme-dark {
      background-color: #000000; /* Black background when menu is open in dark mode */
      --nav-background-color: #000000;
      --text-color: #ffffff; /* White text when menu is open in dark mode */
      --logo-fill: #ffffff; /* White logo when menu is open in dark mode */
    }
    &.visa-navigation-theme-light {
      background-color: #ffffff; /* White background when menu is open in light mode */
      --nav-background-color: #ffffff;
      --text-color: #1a1a1a; /* Black text when menu is open in light mode */
      --logo-fill: #1434cb; /* Blue logo when menu is open in light mode */
    }
  }
}

.visa-navigation-logo {
  width: 72px;
  margin-right: 32px;
  margin: 16px 48px;
  pointer-events: auto; /* Ensure logo can receive mouse events */

  svg {
    width: 100%;
    height: auto;

    path {
      fill: var(--logo-fill); /* Use theme variable for logo color */
    }
  }
}

.visa-navigation-top-level-tabs {
  display: flex;
  gap: 16px;
  margin-right: auto;
  height: 100%; /* Ensure tabs container takes full height of header */

  .visa-navigation-tab-container {
    position: relative;
    height: 100%; /* Ensure tab container takes full height of header */
  }
}

/* Desktop-only hover zone covering full navigation width */
.visa-navigation-hover-zone {
  position: absolute; /* Position absolutely to cover full navigation */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%; /* Cover full navigation width */
  height: 100%; /* Take full height of header */
  cursor: pointer; /* Show pointer cursor to indicate interactivity */
  z-index: -1; /* Behind all interactive elements */

  /* Make it invisible but interactive */
  background: transparent;
  pointer-events: auto; /* Enable hover events */

  /* Debug mode - uncomment to visualize the hover zone */
  /* background: rgba(255, 0, 0, 0.1); */

  /* Mobile protection - completely hide on mobile */
  @media (max-width: 1023px) {
    display: none !important;
  }
}

.visa-navigation-top-level-tabs {
  .visa-navigation-tab {
    background: none;
    border: none;
    font-size: 14px;
    font-weight: 400;
    font-family: "Visa Dialect", sans-serif;
    color: var(--text-color); /* Use theme variable for text color */
    cursor: pointer;
    transition: background-color 0.2s;
    position: relative;
    display: flex;
    align-items: center;
    height: 100%; /* Ensure tab takes full height of the header */
    box-sizing: border-box;
    pointer-events: auto; /* Ensure tabs can receive mouse events */

    &:hover,
    &.visa-navigation-hover-zone-active {
      &::before {
        content: "";
        position: absolute;
        top: 0; /* Position at the top of the tab */
        left: 0;
        width: 100%;
        height: 4px; /* Slightly thicker line for better visibility */
        background-color: var(--text-color); /* Use theme variable for hover line color */
      }
    }

    &.visa-navigation-active {
      font-weight: 500;
    }

    .visa-navigation-tab-content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      text-align: left;
    }

    .visa-navigation-tab-text {
      font-size: 14px;
      font-weight: inherit;
      line-height: 1.2;
    }

    .visa-navigation-tab-description {
      font-size: 12px;
      font-weight: 400;
      color: rgba(var(--text-color-rgb, 22, 22, 22), 0.7);
      line-height: 1.2;
      margin-top: 2px;
    }

    .visa-navigation-tab-arrow {
      margin-left: 4px;
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;

      &.visa-navigation-arrow-down {
        border-top: 5px solid var(--text-color);
        border-bottom: 0;
      }

      &.visa-navigation-arrow-up {
        border-bottom: 5px solid var(--text-color);
        border-top: 0;
      }
    }
  }

  .visa-navigation-submenu {
    position: fixed;
    top: 64px; /* Height of the header */
    left: 0;
    background-color: var(--nav-background-color); /* Use theme variable for background color */
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    width: 100%;
    max-width: 100vw; /* Prevent horizontal overflow */
    height: auto; /* Height based on content */
    box-sizing: border-box; /* Include padding in width calculation */
    display: none; /* Hide by default, will be shown on click */
  }

  /* Only show submenu when it's the active submenu */
  .visa-navigation-submenu.visa-navigation-active {
    display: block;
  }

  .visa-navigation-menu {
    background-color: var(--nav-background-color);
    padding: 48px 48px 48px 48px;
    gap: 32px;
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .visa-navigation-text-link {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start; /* Left align the text */
    cursor: pointer;
    text-align: left; /* Left align the text */
    white-space: nowrap;
    flex-wrap: nowrap;

    .visa-navigation-link {
      font-size: 20px;
      font-weight: 500;
      line-height: 1.3em;
      color: var(--text-color); /* Use theme variable for text color */
      margin-right: 8px;
      white-space: nowrap;
    }

    .visa-navigation-arrow-icon {
      display: flex;
      align-items: center;
      color: var(--text-color);
      transition:
        color 0.3s ease,
        margin-right 0.3s ease;
      flex-shrink: 0;
    }
  }

  .visa-navigation-link,
  .visa-navigation-arrow-icon {
    transition:
      color 0.3s ease,
      margin-left 0.3s ease;
  }

  .visa-navigation-text-link:hover .visa-navigation-link,
  .visa-navigation-text-link:hover .visa-navigation-arrow-icon {
    color: #1434cb;
  }

  .visa-navigation-text-link:hover .visa-navigation-arrow-icon {
    margin-left: 6px;
  }

  /* Link wrapper styles for clickable header text */
  .visa-navigation-link-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-decoration: none;
    color: inherit;
    width: 100%;
    white-space: nowrap;
    flex-wrap: nowrap;

    &:hover .visa-navigation-link,
    &:hover .visa-navigation-arrow-icon {
      color: #1434cb;
    }

    &:hover .visa-navigation-arrow-icon {
      margin-left: 6px;
    }
  }

  .visa-navigation-mobile-link-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-decoration: none;
    color: inherit;
    width: 100%;
    white-space: nowrap;
    flex-wrap: nowrap;

    &:hover .visa-navigation-mobile-link,
    &:hover .visa-navigation-mobile-arrow-icon {
      color: #1434cb;
    }

    &:hover .visa-navigation-mobile-arrow-icon {
      margin-left: 6px;
    }
  }

  .visa-navigation-menu-container {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: flex-start;
  }

  .visa-navigation-row {
    display: flex;
    gap: 24px; /* Gap between columns is 24px */
    flex: 1;
    min-width: calc(100% - 250px); /* Leave space for the card */
    box-sizing: border-box;
    justify-content: flex-start; /* Left align the columns */

    /* Tablet breakpoint adjustments - when card is hidden, use full width */
    @media (min-width: 768px) and (max-width: 1024px) {
      min-width: 100%; /* Use full width when card column is hidden */
    }
  }

  .visa-navigation-column {
    display: flex;
    flex-direction: column;
    gap: 16px; /* Gap between list items is 16px */

    &.visa-navigation-column01 {
      min-width: 288px;
      // width: 25%;

      .visa-navigation-navigation.visa-navigation-theme-light & {
        color: #767676; /* Default text color for light background in first column */
      }
    }

    &.visa-navigation-column02,
    .visa-navigation-column03 {
      .visa-navigation-nav-list-item {
        .visa-navigation-nav-item-text {
          &:hover {
            color: #1431cb; /* Change text color on hover */
          }
          &:focus-visible {
            outline: none;
            color: #1431cb; /* Change text color on hover */
          }
        }
      }
      .visa-navigation-nested-menu {
        .visa-navigation-nav-list-item {
          .visa-navigation-container {
            &:hover {
              color: #1431cb;
            }
            &:focus-visible {
              outline: none;
              color: #1431cb; /* Change text color on hover */
            }
          }
        }
      }
    }

    &.visa-navigation-card-column03 {
      flex: 0 0 auto; /* Don't allow this column to grow or shrink */
      width: 300px; /* Set a fixed width to match card width */
      margin-left: 24px; /* Add margin to separate from the navigation columns */
      display: block;
      @media (min-width: 768px) and (max-width: 1025px) {
        display: none;
      }
    }
  }

  .visa-navigation-column:not(.visa-navigation-column01) {
    gap: 8px;
    &:has(.is-icon-desc) {
      gap: 24px;
    }
  }

  .visa-navigation-nav-list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 16px;
    cursor: pointer;
    width: 288px;
    min-height: 32px;
    position: relative;

    // &.visa-navigation-active {
    //   .visa-navigation-container {
    //     color: var(--text-color); /* Active item uses theme color */
    //   }
    // }

    .visa-navigation-container {
      font-size: 16px; /* Consistent font size across all levels */
      font-weight: 500; /* Consistent font weight across all levels */
      line-height: 1.2; /* Consistent line height across all levels */
      color: var(--text-color); /* Use theme variable for text color */
      text-decoration: none;
      display: flex;
      align-items: center;
      justify-content: flex-start; /* Left align the text and icon */
      width: 100%;
      text-align: left; /* Left align the text */
      transition: color 0.3s ease; // smooth color change
    }

    .visa-navigation-container {
      &:has(.visa-navigation-menu-icon-logo) {
        justify-content: flex-start;
      }
    }

    .visa-navigation-menu-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      width: 40px;
      height: 40px;

      img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }

      &.visa-navigation-menu-icon-image img {
        width: 40px;
        height: 40px;
      }
    }

    a.visa-navigation-container {
      text-decoration: none;
      color: var(--text-color);
    }

    .visa-navigation-nav-item-content {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .visa-navigation-nav-item-text {
      font-size: 16px; /* Consistent with main container font size */
      font-weight: 500; /* Consistent with main container font weight */
      line-height: 1.2; /* Consistent with main container line height */
      color: var(--text-color);
    }

    .visa-navigation-external-icon {
      color: var(--text-color);
      align-self: flex-start;
      margin-left: auto;
    }

    .visa-navigation-nav-item-description {
      font-size: 12px;
      font-weight: 400;
      line-height: 1.3;
      color: rgba(var(--text-color-rgb, 22, 22, 22), 0.7);
    }

    .visa-navigation-heading-container {
      font-size: 16px; /* Consistent font size */
      font-weight: 500; /* Consistent font weight */
      line-height: 1.5; /* Consistent line height */
      text-align: left; /* Left align the heading text */
      width: 100%; /* Ensure the heading takes full width */
      justify-content: flex-start; /* Left align the heading text */
    }

    .visa-navigation-slot {
      display: flex;
      align-items: center;
      color: var(--text-color); /* Use theme variable for text color */

      /* Rotate arrow when submenu is active */
      .visa-navigation-rotate-down {
        transform: rotate(90deg);
        transition: transform 0.2s ease;
      }
    }
  }

  .visa-navigation-column:has(.visa-navigation-nav-item-active) {
    .visa-navigation-nav-list-item:not(.visa-navigation-nav-item-active) {
      .visa-navigation-container {
        color: #767676;
      }
      .visa-navigation-nav-item-text {
        color: #767676;
      }
    }
  }

  .visa-navigation-nav-list-item {
    .visa-navigation-container {
      color: #1a1a1a;
      transition: color 0.3s ease;
    }

    &.visa-navigation-nav-item-active {
      .visa-navigation-container {
        color: var(--text-color);
      }
    }
  }

  .visa-navigation-column {
    position: relative;

    .active-highlight {
      position: absolute;
      bottom: 0;
      height: 3px;
      background: var(--text-color);
      transition:
        left 0.3s ease,
        width 0.3s ease;
    }
  }

  .visa-navigation-nav-list-item {
    position: relative;
    cursor: pointer;

    // &.visa-navigation-active {
    //   color: var(--text-color);
    // }
  }

  /* Nested menu styling */
  .visa-navigation-nested-menu {
    display: none; /* Hide by default */
    position: absolute;
    left: 100%;
    top: 0;
    width: 288px; /* Fixed width consistent with L1 and L2 */
    min-width: 288px; /* Ensure minimum width matches L1 and L2 */
    z-index: 10001; /* Highest z-index to ensure nested menus appear above all other content */
    background-color: var(--nav-background-color); /* Consistent background */
    /* Remove border-radius and box-shadow for consistent experience */
    pointer-events: auto; /* Ensure nested menu can receive pointer events */

    /* Add consistent gap between nested menu items - same as columns */
    display: flex;
    flex-direction: column;
    gap: 16px; /* Same 16px gap as used between columns */
  }

  /* Nested menus are now shown via JavaScript on click, not on hover */
  /* Show nested menu when it's rendered by the component */
  .visa-navigation-nested-menu {
    display: flex; /* Changed from block to flex to support gap */
    flex-direction: column;
    gap: 16px; /* Consistent 16px gap between nested items */
  }

  /* Nested menu items styling - consistent with L1 and L2 */
  .visa-navigation-nested-menu .visa-navigation-nav-list-item {
    width: 288px; /* Fixed width consistent with L1 and L2 */
    margin: 0; /* Remove any margin */
    border-radius: 16px; /* Consistent border radius */

    .visa-navigation-container {
      &:has(.visa-navigation-menu-icon-logo) {
        justify-content: flex-start;
      }
    }

    &.visa-navigation-active {
      .visa-navigation-container {
        color: var(--text-color);
      }
    }
  }

  .visa-navigation-column02 {
    overflow-y: auto;
    max-height: 70vh;
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    & .visa-navigation-container {
      font-size: 14px;
    }
    & .visa-navigation-nav-item-text {
      font-size: 14px;
    }
  }

  .visa-navigation-column02 {
    &:has(.is_active) {
      overflow-y: visible;
    }
  }

  .visa-navigation-column-layout-2 {
    display: flex;
    flex-direction: row;
    gap: 24px;
    & .visa-navigation-container {
      font-size: 14px;
    }
    & .visa-navigation-nav-item-text {
      font-size: 14px;
    }
  }

  .visa-navigation-column-layout-2-left {
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    max-height: 70vh;
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .visa-navigation-column-layout-2-right {
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    max-height: 70vh;
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  /* Adjust position for deeper nested menus with consistent spacing */
  .visa-navigation-nested-level-2 {
    left: calc(100% + 16px); /* Full width offset plus consistent 16px gap */
    top: 0;
  }

  .visa-navigation-nested-level-3 {
    left: calc(100% + 16px); /* Full width offset plus consistent 16px gap */
    top: 0;
  }

  .visa-navigation-nested-level-4 {
    left: calc(100% + 16px); /* Full width offset plus consistent 16px gap */
    top: 0;
  }

  /* Ensure all nested menus have consistent spacing from their parent */
  .visa-navigation-nested-menu {
    left: calc(100% + 16px); /* Consistent 16px gap between all nested levels */
    top: 0;
  }

  // .visa-navigation-nav-item-parent .visa-navigation-container {
  //   --text-color: #767676; /* Default text color for parent items */
  // }

  .visa-navigation-navigation.visa-navigation-theme-dark
    .visa-navigation-nav-item-parent
    .visa-navigation-container {
    --text-color: #999999;
  }

  .visa-navigation-card {
    height: 436px; /* Set exact height as requested */
    width: 300px; /* Set exact width as requested */
    text-align: center; /* Center the card content */
    padding-right: 48px; /* Add padding as requested */
    box-sizing: border-box;

    .visa-navigation-card-container {
      aspect-ratio: 3/4;
      background-color: var(--nav-background-color);
      margin-bottom: 12px;
      overflow: hidden;
      height: 80%; /* Take most of the card height */
      width: 100%; /* Take full width of the card */
    }

    .visa-navigation-card-image {
      width: 100%;
      height: 100%;
      background-size: cover;
      background-position: center;
    }

    .visa-navigation-copy-container {
      text-align: center; /* Center the copy content */

      .visa-navigation-title {
        font-size: 14px;
        line-height: 1.4;
        color: var(--text-color); /* Use theme variable for text color */
        font-weight: 400;
        text-align: left; /* Center the title text */
        transition: color 0.3s ease; /* Add smooth transition */
      }
    }
  }

  .visa-navigation-card-link {
    cursor: pointer;
    text-decoration: none;

    &:hover .visa-navigation-title {
      color: #1434cb;
    }
  }

  .visa-navigation-card-link .visa-navigation-title {
    color: blue;
    text-decoration: none;
    transition: color 0.3s ease;
  }

  .visa-navigation-card-column03:not(.visa-navigation-card-link) .visa-navigation-title {
    cursor: default;
  }

  // Keep original styles for backward compatibility
  .visa-navigation-submenu-section {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .visa-navigation-submenu-heading {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: #1434cb;
  }

  .visa-navigation-submenu-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .visa-navigation-submenu-item {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .visa-navigation-submenu-link {
    color: var(--text-color); /* Use theme variable for text color */
    text-decoration: none;
    font-size: 12px;
    transition: color 0.2s;
  }

  .visa-navigation-submenu-image-section {
    text-align: center;
  }

  .visa-navigation-submenu-image-link {
    display: block;
    text-decoration: none;
    color: var(--text-color);
  }

  .visa-navigation-submenu-image {
    max-width: 100%;
    height: auto;
    margin-bottom: 8px;
    border-radius: 4px;
  }

  .visa-navigation-submenu-image-text {
    display: block;
    font-size: 12px;
    font-weight: 500;
  }

  /* Feature Cards Styles */
  .visa-navigation-feature-cards-container {
    display: flex;
    gap: 24px; /* Same gap as existing columns */
    flex-wrap: wrap;

    /* Tablet breakpoint adjustments - completely change layout to prevent overlapping */
    @media (min-width: 768px) and (max-width: 1024px) {
      flex-direction: column; /* Stack columns vertically */
      gap: 16px; /* Reduce gap to save space */
      max-width: 100%; /* Ensure it doesn't exceed container */
      overflow: visible; /* Allow content to be visible */
      flex-wrap: nowrap; /* Don't wrap on tablet */
    }
  }

  .visa-navigation-feature-card-column {
    flex: 1 1 200px;
    min-width: 200px;

    /* Tablet breakpoint adjustments - make columns stack properly */
    @media (min-width: 768px) and (max-width: 1024px) {
      flex: none; /* Don't flex on tablet */
      min-width: auto; /* Remove min-width constraint */
      width: 100%; /* Take full width */
      max-width: 100%; /* Ensure it doesn't exceed container */
      box-sizing: border-box; /* Include padding in width */
    }
  }

  .visa-navigation-feature-card-heading {
    font-size: 12px;
    font-weight: 500;
    margin: 0 0 16px 0;
    color: #656565;
    line-height: 1.5;

    /* Tablet breakpoint adjustments */
    @media (min-width: 768px) and (max-width: 1024px) {
      margin: 0 0 8px 0; /* Reduce margin to save space */
      font-size: 11px; /* Slightly smaller font */
    }
  }

  .visa-navigation-feature-card-items {
    display: flex;
    flex-direction: column;
    gap: 16px; /* Same gap as existing nav items */

    /* Tablet breakpoint adjustments */
    @media (min-width: 768px) and (max-width: 1024px) {
      gap: 8px; /* Reduce gap to prevent overlapping */
    }
  }
}

.visa-navigation-secondary-nav {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 48px;
  pointer-events: auto; /* Ensure secondary nav can receive mouse events */
  position: relative; /* Ensure proper stacking context */
  z-index: 10; /* Above hover zone */
}

.visa-navigation-icon-buttons {
  display: flex;
  gap: 8px;
  pointer-events: auto; /* Ensure icon buttons can receive mouse events */
}

.visa-navigation-icon-button {
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
  pointer-events: auto; /* Ensure individual buttons can receive mouse events */

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  svg {
    width: 24px;
    height: 24px;
  }

  &.visa-navigation-play-pause-button svg path,
  &.visa-navigation-search-button svg path,
  &.visa-navigation-bookmark-button svg path {
    fill: var(--text-color); /* Use theme variable for icon color */
  }
}

/* Remove conflicting styles */

.visa-navigation-sign-in-button {
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 400;
  color: var(--text-color); /* Use theme variable for text color */
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
  height: 100%; /* Ensure button takes full height of the header */
  display: flex;
  align-items: center;
  box-sizing: border-box;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.visa-navigation-divider {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background-color: var(--text-color);
  opacity: 0.1;
}

.visa-navigation-submenu-overlay {
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 999;
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
}

.visa-navigation-loading {
  padding: 20px;
  text-align: center;
  font-weight: bold;
}

/* Search overlay styles */
.visa-navigation-search-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10;
  background-color: var(--nav-background-color);
  transition: opacity 0.3s ease;

  &.visa-navigation-desktop {
    height: 100%; /* Same height as navigation container */
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.visa-navigation-mobile {
    position: fixed;
    height: 100vh;
    width: 100vw;
    z-index: 10002;
    display: flex;
    flex-direction: column;
  }
}

/* Desktop search overlay styles */
.visa-navigation-search-overlay.visa-navigation-desktop {
  .visa-navigation-search-container {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 24px 8px 8px; /* Add 24px padding on the right */
    position: relative;
  }

  .visa-navigation-search-field {
    background-color: var(--search-field-bg, #f5f5f5);
    border-radius: 1000px;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 500px;
  }

  .visa-navigation-search-content {
    width: 100%;
  }

  .visa-navigation-search-form {
    display: flex;
    align-items: flex-end;
  }

  .visa-navigation-search-icon {
    align-items: center;
    margin-right: 8px;
    color: var(--search-icon-color, #666666);
    outline: none;
    background: transparent;
    border: none;
    cursor: pointer;
  }

  .visa-navigation-search-input-container {
    flex: 1;
  }

  .visa-navigation-search-input {
    font-family: "Visa Dialect", sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.25em;
    color: var(--search-text-color, #333333);
    width: 100%;
    border: none;
    background: transparent;
    outline: none;
    padding: 0;

    &::placeholder {
      color: var(--search-placeholder-color, #666666);
    }
  }

  .visa-navigation-search-close {
    position: absolute;
    right: 24px; /* Position 24px from the right edge */
    display: flex;
    align-items: center;
  }

  .visa-navigation-search-close-button {
    background: none;
    border: none;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 50%;
    color: var(--search-icon-color, #666666);
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
}

/* Mobile search overlay styles */
.visa-navigation-search-overlay.visa-navigation-mobile {
  .visa-navigation-mobile-search-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    background-color: var(--nav-background-color);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .visa-navigation-mobile-search-back,
  .visa-navigation-mobile-search-close {
    background: none;
    border: none;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--text-color);

    svg {
      width: 24px;
      height: 24px;
    }
  }

  .visa-navigation-mobile-search-content {
    flex: 1;
    padding: 24px;
    display: flex;
    flex-direction: column;
  }

  .visa-navigation-search-container {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .visa-navigation-search-field {
    background-color: var(--search-field-bg, #f5f5f5);
    border-radius: 1000px;
    padding: 16px;
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 600px;
  }

  .visa-navigation-search-content {
    width: 100%;
  }

  .visa-navigation-search-form {
    display: flex;
    align-items: flex-end;
    flex-direction: row;
    justify-content: center;
  }

  .visa-navigation-search-icon {
    align-items: center;
    margin-right: 8px;
    color: var(--search-icon-color, #666666);
    outline: none;
    background: transparent;
    border: none;
    cursor: pointer;
  }

  .visa-navigation-search-input-container {
    flex: 1;
  }

  .visa-navigation-search-input {
    font-family: "Visa Dialect", sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.25em;
    color: var(--search-text-color, #333333);
    width: 100%;
    border: none;
    background: transparent;
    outline: none;
    padding: 0;

    &::placeholder {
      color: var(--search-placeholder-color, #666666);
    }
  }
}

/* Theme-aware search colors */
:host(.visa-navigation-theme-dark) {
  --search-field-bg: #2a2a2a;
  --search-text-color: #ffffff;
  --search-placeholder-color: #999999;
  --search-icon-color: #ffffff;
}

:host(.visa-navigation-theme-light) {
  --search-field-bg: #f5f5f5;
  --search-text-color: #333333;
  --search-placeholder-color: #666666;
  --search-icon-color: #1434cb;
}

/* Mobile theme overrides */
.visa-navigation-search-overlay.visa-navigation-mobile {
  &.visa-navigation-theme-dark {
    background-color: #000000;
    --search-field-bg: #2a2a2a;
    --search-text-color: #ffffff;
    --search-placeholder-color: #999999;
    --search-icon-color: #ffffff;
  }

  &.visa-navigation-theme-light {
    background-color: #ffffff;
    --search-field-bg: #f5f5f5;
    --search-text-color: #333333;
    --search-placeholder-color: #666666;
    --search-icon-color: #1434cb;
  }
}

/* Body class to prevent scrolling when menu is open */
body.visa-navigation-no-scroll {
  overflow: hidden;
}

/* Media Queries */
/* Hamburger menu button */
.visa-navigation-hamburger-button {
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
  margin-left: 8px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  svg {
    width: 24px;
    height: 24px;

    path {
      fill: var(--text-color); /* Use theme variable for icon color */
    }
  }
}

/* Mobile menu overlay */
.visa-navigation-mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: var(--nav-background-color, #ffffff);
  z-index: 10002;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  /* Ensure proper scrolling on all mobile devices */
  -webkit-overflow-scrolling: touch;
}

/* Hide the main header when mobile menu is open */
// body.mobile-menu-open .visa-navigation-navigation {
//   display: none;
// }

/* Ensure the mobile menu uses the same theme colors as desktop */
.visa-navigation-mobile-menu-overlay {
  &.visa-navigation-theme-dark {
    background-color: #000000; /* Black background in dark mode */
    --text-color: #ffffff; /* White text in dark mode */
  }
  &.visa-navigation-theme-light {
    background-color: #ffffff; /* White background in light mode */
    --text-color: #1a1a1a; /* Black text in light mode */
  }
}

/* Mobile menu */
.visa-navigation-mobile-menu {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  & .visa-navigation-mobile-text-link {
    display: flex;
    align-items: center;
    justify-content: flex-start; /* Left align the text */
    cursor: pointer;
    text-align: left; /* Left align the text */
    padding: 16px 24px 0px 24px;
    white-space: nowrap;
    flex-wrap: nowrap;

    & .visa-navigation-mobile-link {
      font-size: 20px;
      font-weight: 500;
      line-height: 1.3em;
      color: var(--text-color); /* Use theme variable for text color */
      margin-right: 8px;
      white-space: nowrap;
    }

    & .visa-navigation-mobile-arrow-icon {
      display: flex;
      align-items: center;
      color: var(--text-color);
      flex-shrink: 0;
    }
    & a {
      display: inline-flex;
    }
  }
}

/* Mobile menu header */
.visa-navigation-mobile-menu-header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  // border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  background-color: var(--nav-background-color, #ffffff);
  z-index: 10;
}

.visa-navigation-mobile-menu-back {
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-right: 8px;

  svg {
    width: 24px;
    height: 24px;

    path {
      fill: var(--text-color); /* Use theme variable for icon color */
    }
  }
}

.visa-navigation-mobile-menu-title {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-color);
  text-align: center;
}

.visa-navigation-mobile-menu-close {
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;

    path {
      fill: var(--text-color); /* Use theme variable for icon color */
    }
  }
}

/* Mobile menu content */
.visa-navigation-mobile-menu-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Allow flex item to shrink below content size */
  max-height: calc(100vh - 80px); /* Subtract header height to prevent overflow */
}

.visa-navigation-mobile-menu-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  &:has(.is-icon-desc) {
    gap: 24px;
  }
}

.visa-navigation-mobile-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  /* Removed hover effect for mobile - touch devices don't need hover states */
}

.visa-navigation-mobile-menu-item-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex: 1;
}
.visa-navigation-mobile-menu-item-content_info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.visa-navigation-mobile-menu-item-text {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  color: var(--text-color);
  min-height: 32px;
}

.visa-navigation-mobile-menu-item-text.is-icon-desc {
  min-height: auto;
}

.visa-navigation-mobile-menu-item-text.level-1 {
  font-size: 16px;
}

/* Mobile featureCard title styling */
.visa-navigation-mobile-menu-item-text.feature-card-title {
  font-size: 12px;
  color: #656565;
}

.visa-navigation-mobile-menu-item-description {
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  color: rgba(
    22,
    22,
    22,
    0.7
  ) !important; /* Force dark text for mobile descriptions, overriding theme variables */
}

/* Ensure mobile menu descriptions are always visible regardless of header theme */
.visa-navigation-mobile-menu-overlay.visa-navigation-theme-dark
  .visa-navigation-mobile-menu-item-description {
  color: rgba(255, 255, 255, 0.7) !important; /* Light text for dark theme mobile menu */
}

.visa-navigation-mobile-menu-item-chevron {
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;

    path {
      fill: var(--text-color); /* Use theme variable for icon color */
    }
  }
}

/* Mobile menu card */
.visa-navigation-mobile-menu-card {
  margin-top: 24px;
  padding-top: 24px;
  display: flex;
  flex-direction: column;
}

/* Mobile menu card - show by default, then hide at specific levels */
.visa-navigation-mobile-menu-card {
  margin-top: 24px;
  padding-top: 24px;
  display: flex;
  flex-direction: column;
}

/* Hide at level 0 - when no back button (Personal/Business selection) */
.visa-navigation-mobile-menu-overlay:not(:has(.visa-navigation-mobile-menu-back))
  .visa-navigation-mobile-menu-card {
  display: none !important;
}

/* Hide at level 1 - more precise targeting based on menu structure */
/* Hide when back button exists and title matches known level 1 patterns */
.visa-navigation-mobile-menu-overlay:has(.visa-navigation-mobile-menu-back):has(
    .visa-navigation-mobile-menu-title:empty
  )
  .visa-navigation-mobile-menu-card {
  display: none !important;
}

/* Hide when we're at level 1 - specifically target the first level submenus */
/* This targets menus that have back button but are still at the first navigation level */
.visa-navigation-mobile-menu-overlay[data-level="1"] .visa-navigation-mobile-menu-card {
  display: none !important;
}

.visa-navigation-mobile-menu-card-container {
  width: 100%;
  aspect-ratio: 4/3;
  overflow: hidden;
  margin-bottom: 12px;
}

.visa-navigation-mobile-menu-card-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}

.visa-navigation-mobile-menu-card-title {
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  margin-top: 8px;
}

.visa-navigation-mobile-menu-card-link {
  color: var(--text-color);
  text-decoration: none;
  text-align: left;
  &:hover {
    text-decoration: underline;
  }
}

.visa-navigation-mobile-menu-card-link-wrapper {
  display: block;
  text-decoration: none;
  color: inherit;
  width: 100%;

  &:hover {
    text-decoration: none;
  }

  &:hover .visa-navigation-mobile-menu-card-text {
    color: #1434cb;
    text-decoration: underline;
  }
}

.visa-navigation-mobile-menu-card-text {
  color: var(--text-color);
  text-decoration: none;
  transition: color 0.3s ease;
}

.visa-navigation-nav-list-item:not(.visa-navigation-nav-item-active):hover {
  .visa-navigation-heading-container,
  .visa-navigation-slot,
  .visa-navigation-nav-item-content,
  .visa-navigation-external-icon {
    transition:
      color 0.3s ease,
      margin-right 0.3s ease;
  }
  & .visa-navigation-heading-container {
    color: #1434cb;
  }
  & .visa-navigation-slot {
    color: #1434cb;
    margin-right: 6px;
  }
  & .visa-navigation-nav-item-content {
    color: #1434cb;
  }
  & .visa-navigation-external-icon {
    color: #1434cb;
    margin-right: 6px;
  }
  & .visa-navigation-nav-item-text {
    color: #1434cb;
  }

  & .visa-navigation-container {
    color: #1434cb;
  }
}

.visa-navigation-nav-list-item:not(.visa-navigation-nav-item-active):focus-visible {
  outline: none;
  .visa-navigation-heading-container,
  .visa-navigation-slot,
  .visa-navigation-nav-item-content,
  .visa-navigation-external-icon {
    transition:
      color 0.3s ease,
      margin-right 0.3s ease;
  }
  & .visa-navigation-heading-container {
    color: #1434cb;
  }
  & .visa-navigation-slot {
    color: #1434cb;
    margin-right: 6px;
  }
  & .visa-navigation-nav-item-content {
    color: #1434cb;
  }
  & .visa-navigation-external-icon {
    color: #1434cb;
    margin-right: 6px;
  }
  & .visa-navigation-nav-item-text {
    color: #1434cb;
  }

  & .visa-navigation-container {
    color: #1434cb;
    outline: none;
  }
}

.visa-navigation-nav-item-active:hover {
  outline: none;
  cursor: default;
  & .visa-navigation-heading-container {
    color: #1434cb;
  }
}
.visa-navigation-nav-list-item.visa-navigation-nav-item-active:hover {
  outline: none;
  cursor: default;
  & .visa-navigation-nav-item-text {
    color: var(--text-color);
  }
}

.visa-navigation-nav-item-active:focus-visible {
  outline: none;
  cursor: default;
  & .visa-navigation-heading-container {
    color: #1434cb;
  }
}
.visa-navigation-nav-list-item.visa-navigation-nav-item-active:focus-visible {
  outline: none;
  cursor: default;
  & .visa-navigation-nav-item-text {
    color: var(--text-color);
  }
}

@media (max-width: 1023px) {
  .visa-navigation-navigation {
    background-color: var(
      --nav-background-color
    ); /* Default to transparent background for mobile too */
    display: flex;
    align-items: center;
    padding: 16px 24px;
    height: 64px;
    position: relative;
    width: 100%;
    max-width: 100vw; /* Prevent horizontal overflow */
    box-sizing: border-box; /* Include padding in width calculation */
    overflow: visible; /* Allow hover effects to extend beyond the navigation container */
    justify-content: space-between; /* Space items evenly */

    .visa-navigation-secondary-nav-container {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    &.visa-navigation-menu-open {
      &.visa-navigation-theme-dark {
        background-color: #000000; /* Black background when menu is open in dark mode */
      }
      &.visa-navigation-theme-light {
        background-color: #ffffff; /* White background when menu is open in light mode */
      }
    }

    .visa-navigation-logo {
      margin: 0; /* Remove margin in mobile view */
    }
  }

  .visa-navigation-top-level-tabs {
    display: none; /* Hide tabs in mobile view */
  }

  .visa-navigation-secondary-nav {
    display: flex;
    align-items: center;
    padding: 0; /* Remove padding in mobile view */

    .visa-navigation-sign-in-button {
      display: none; /* Hide sign in button in mobile view */
    }
  }

  .visa-navigation-secondary-nav-container {
    display: flex;
    align-items: center;
  }

  .visa-navigation-search-container form {
    flex-direction: column;
  }

  .visa-navigation-search-button,
  .visa-navigation-cancel-button {
    width: 100%;
  }
}
